/**
 * MLEADER CONTEXT_DATA nesting-depth tracking — single source of truth.
 *
 * An AcDbMLeader entity embeds a `CONTEXT_DATA` block (the MLeaderContext /
 * MTextData / BlockData record per the ezdxf MULTILEADER reference). The block
 * is delimited by string-control group markers that nest:
 *
 *   300 "CONTEXT_DATA{"  opens the context block        (depth +1)
 *   301 "{"              opens a nested sub-block         (depth +1)
 *   301 "}"              closes the context/sub-block     (depth -1)
 *   302 "LEADER{"        opens a leader sub-record       (depth +1)
 *   303 "}"              closes a leader sub-record       (depth -1)
 *   304 "LEADER_LINE{"   opens a leader-line sub-record  (depth +1)
 *   305 "}"              closes a leader-line sub-record  (depth -1)
 *
 * The numeric group codes that appear INSIDE this block (40, 41, 44, 90, 91,
 * 92, 93, 170, 171, 172, 173, 290, 291, …) share their values with the
 * AcDbMLeader BODY proper, but carry COMPLETELY DIFFERENT semantics. A flat
 * entity-body reader that fires on a CONTEXT_DATA-scoped code mis-places the
 * value onto the wrong typed field and corrupts the MLEADER on round-trip
 * (the #256 F2 class of bug — e.g. an in-context `44` landing on
 * `geometry.landingGap`). Body readers must therefore only fire at depth 0.
 *
 * Legacy note: the dxf-io MLEADER writer historically synthesised a SIMPLIFIED
 * CONTEXT_DATA where the leader-line sub-record was opened with `302
 * "LEADER_LINE{"` and every block closed with `303 "}"`. Older fixtures and
 * the parser's own re-serialised output use that convention. To keep both the
 * real-AutoCAD/ezdxf format AND the legacy synthetic format working, the
 * matcher recognises a leader-line open on EITHER the real `304 "LEADER_LINE{"`
 * OR the legacy `302 "LEADER_LINE{"`; a `303 "}"` (legacy context/leader close)
 * and a `301 "}"` / `305 "}"` (real closes) are all treated as closes.
 *
 * CRITICAL (Codex #258 F2): group 304 is DUAL-USE inside CONTEXT_DATA. Besides
 * the `304 "LEADER_LINE{"` open marker it is ALSO the MLeader MTEXT CONTENTS
 * field (ezdxf MTextData.default_content), e.g. a real fixture carries both
 * `304 "Test MText"` (content) AND `304 "LEADER_LINE{"` (marker) in the SAME
 * entity. The old matcher fired on ANY 304 whose value ends in `{`, so an MTEXT
 * content string that merely ended with a literal/escaped `{` would be a FALSE
 * open marker (corrupting nesting depth + losing the text). The matcher
 * therefore keys off the EXACT recognised marker tokens — `CONTEXT_DATA{`,
 * `LEADER{`, `LEADER_LINE{` and the bare `}` close — NOT any trailing `{`. An
 * arbitrary `304 <text>` is left for the caller's content reader.
 *
 * CRITICAL (Codex #259 P2): group 301 is DUAL-USE as a PURE STRUCTURAL brace
 * marker. Inside CONTEXT_DATA the real ezdxf/AutoCAD format opens a nested
 * sub-block (e.g. a BlockData / leader sub-record group) with `301 "{"` and
 * closes it (and the CONTEXT_DATA scope itself) with `301 "}"`. The previous
 * classifier handled `301 "}"` (close) ONLY; a nested `301 "{"` open fell
 * through to `undefined`, so depth never incremented on the open and the FIRST
 * `301 "}"` close dropped depth to 0 — ending the context EARLY and skipping
 * every top-level field after the sub-block. Unlike the content-dual 304 (which
 * carries MTEXT text AND a LEADER_LINE{ marker, and so is named-token + depth
 * gated), 301 is NEVER a text-content code — its value is ALWAYS a structural
 * brace — so it is safe to classify value-aware by the brace: `301 "{"` (or any
 * value ending in `{`) → OPEN nested sub-block; `301 "}"` → CLOSE. 303 / 305
 * are pure CLOSES that pair with the named-token opens (302 LEADER{ /
 * 304 LEADER_LINE{); they are never emitted with a `{` to open, so they stay
 * close-only and are NOT generalized to brace-open.
 */
import type { DxfGroup } from "./types";

/**
 * Exact open-marker token → set of group codes that legitimately carry it.
 * Only these EXACT (whitespace-trimmed) tokens open a CONTEXT_DATA scope; a
 * 304 with any other value (e.g. an MTEXT content string) is NOT a marker.
 */
const OPEN_MARKERS: ReadonlyMap<string, ReadonlySet<number>> = new Map([
  ["CONTEXT_DATA{", new Set([300])],
  ["LEADER{", new Set([302])],
  // Real ezdxf/AutoCAD emits LEADER_LINE{ on 304; legacy synthetic writer
  // emitted it on 302. Accept both source codes for this exact token.
  ["LEADER_LINE{", new Set([302, 304])],
]);

/**
 * Tokens whose open marker is structural ONLY inside an already-open scope
 * (current depth > 0). The writer emits a BODY-level group 304 carrying the
 * MLeader MTEXT contents (ezdxf MTextData.default_content) BEFORE any
 * CONTEXT_DATA block — and that content may itself be the literal string
 * "LEADER_LINE{". At depth 0 such a 304 is body textContents, NOT a nesting
 * marker, so treating it as an opener would skip the 304 content reader and
 * lose the text while desyncing depth (Codex #258 F3). Only `CONTEXT_DATA{`
 * legitimately opens from depth 0; the leader-line marker may only open within
 * an existing context/leader scope.
 */
const DEPTH_GATED_OPEN_TOKENS = new Set(["LEADER{", "LEADER_LINE{"]);

/** Group codes that, with a bare `}` value, CLOSE a CONTEXT_DATA scope. */
const CLOSE_CODES = new Set([301, 303, 305]);

/**
 * Pure-structural brace codes that are dual-use: `<code> "{"` OPENS a nested
 * sub-block and `<code> "}"` CLOSES it. Currently only group 301 — the real
 * ezdxf/AutoCAD CONTEXT_DATA opens nested sub-records with `301 "{"` and closes
 * them with `301 "}"` (#259 P2). 303 / 305 are NOT here: they only ever close
 * (they pair with the named-token 302 "LEADER{" / 304 "LEADER_LINE{" opens),
 * never carrying a `{` to open. These codes are value-aware: the SAME code is
 * an open or a close purely by its brace value, never text content (contrast
 * 304, which is content-dual and so named-token + depth gated).
 */
const BRACE_OPEN_CODES = new Set([301]);

/**
 * If `group` is a CONTEXT_DATA nesting marker, return the new depth
 * (incremented on a recognised opening marker token, clamped-decremented on a
 * `}` close marker); otherwise return `undefined` so the caller falls through
 * to its own per-code handling (e.g. the 304 MTEXT-content reader).
 *
 * Callers use the `undefined` result as the "not a nesting marker, keep
 * processing" signal. Matching is restricted to the EXACT known marker tokens
 * so a dual-use code (304 = LEADER_LINE{ marker OR MTEXT content) is only
 * treated as a marker when its value is the literal token (#258 F2) AND, for
 * the depth-gated tokens (LEADER{ / LEADER_LINE{), only when already inside an
 * open scope (current depth > 0) — a depth-0 304 "LEADER_LINE{" is body
 * textContents, not a marker (#258 F3).
 */
export function advanceMLeaderContextDepth(
  group: DxfGroup,
  depth: number,
): number | undefined {
  const marker = classifyMLeaderMarker(group, depth);
  if (marker === undefined) return undefined;
  return marker.kind === "close"
    ? Math.max(0, depth - 1)
    : depth + 1;
}

/**
 * Structural CONTEXT_DATA marker semantics — the single source of truth for
 * BOTH depth tracking (`advanceMLeaderContextDepth`) and hierarchy building
 * (`buildMLeaderRoots`) / raw-group extraction (`extractAnnotativeContexts`,
 * `extractBlockAttributeReferences`). Every MLEADER depth-tracking site routes
 * through this one classifier so real (300/301/302/303/304/305) and legacy
 * synthetic (302 "LEADER_LINE{" + 303 "}") markers are recognised identically.
 */
export type MLeaderMarker =
  | { kind: "open"; scope: "context" | "leader" | "leaderLine" | "nested" }
  | { kind: "close" };

/**
 * If `group` is a recognised CONTEXT_DATA structural marker at the given
 * `depth`, return its classification; otherwise `undefined` (not a marker —
 * the caller keeps its own per-code handling, e.g. the 304 MTEXT content
 * reader). Open markers are keyed off the EXACT token (#258 F2) and the
 * leader / leader-line tokens are depth-gated so a depth-0 304 "LEADER_LINE{"
 * is treated as body content, not a marker (#258 F3). A pure-structural brace
 * code (301) is value-aware: `301 "{"` opens a nested sub-block, `301 "}"`
 * closes (#259 P2).
 */
export function classifyMLeaderMarker(
  group: DxfGroup,
  depth: number,
): MLeaderMarker | undefined {
  const token = group.value.trim();
  const openCodes = OPEN_MARKERS.get(token);
  if (openCodes && openCodes.has(group.code)) {
    // A leader / leader-line open marker is only structural inside an
    // already-open scope; at depth 0 it is body content, not a marker.
    if (depth === 0 && DEPTH_GATED_OPEN_TOKENS.has(token)) {
      return undefined;
    }
    // eslint-disable-next-line security/detect-possible-timing-attacks -- DXF marker token literals, not secret comparisons
    if (token === "CONTEXT_DATA{") return { kind: "open", scope: "context" };
    // eslint-disable-next-line security/detect-possible-timing-attacks -- DXF marker token literals, not secret comparisons
    if (token === "LEADER{") return { kind: "open", scope: "leader" };
    return { kind: "open", scope: "leaderLine" };
  }
  // Pure-structural brace open (#259 P2): a 301 whose value is/ends-with `{`
  // OPENS a nested sub-block inside CONTEXT_DATA. Checked BEFORE the close
  // branch so the dual-use `301 "{"` open is not mistaken for a `301 "}"`
  // close (which the close branch handles next). 301 never carries text
  // content, so a value-aware brace test is safe (no content-dual ambiguity).
  if (BRACE_OPEN_CODES.has(group.code) && token.endsWith("{")) {
    return { kind: "open", scope: "nested" };
  }
  if (CLOSE_CODES.has(group.code) && token === "}") {
    return { kind: "close" };
  }
  return undefined;
}
